import { getUrlParam, runUserSearch, replaceAnalyticsTokens, isSuspectedBot } from "./tracker-functions.js";

((Drupal, once) => {
  let initialised = false;

  const mmInstances = {};

  /**
   * Get a media manager player object by its container ID.
   * @param {string} containerId
   * @return The media manager player object
   */
  window.getMediaManagerInstance = (containerId) => {
    return mmInstances[containerId] || null;
  };

  /**
   * Check the player.js script is accessible.
   * @param {object} settings drupal settings object
   */
  function checkPlayerAccessibility(settings) {

    if (!settings?.media_manager?.library) {
      return;
    }
    const url = settings.media_manager.library;

    fetch(`${url}/player.js`)
      .then((response) => {
        if (!response.ok) {
          console.error("Unable to retrieve player.js", response);
          if (settings?.watchsnag?.releaseStage !== "production" && window.Bugsnag) {
            Bugsnag.notify("Unable to retrieve player.js", (event) => {
              event.addMetadata("mediamanager", {
                response,
                playerUrl: url,
              });
            });
          };
          initialised = true;
        }
      })
      .catch((error) => {
        console.log("Error accessing the file:", error);
        if (settings?.watchsnag?.releaseStage !== "production" && window.Bugsnag) {
          Bugsnag.notify("Unable to retrieve player.js", (event) => {
            event.addMetadata("mediamanager", {
              error,
              playerUrl: url,
            });
          });
        }
        initialised = true;
      });
  }

  /**
   * Check the data required for elastic tracker has been created.
   * @returns {number} Number of milliseconds instruct setTimeout to pause
   *   before continuing.
   */
  function checkElasticReady(settings, element) {
    if (!settings?.elastic_tracker?.track[0].user_key) {
      console.log("Elastic Tracker Not Initialised");
      if (settings?.watchSnag?.releaseStage && settings.watchSnag.releaseStage !== "production" && window.Bugsnag) {
        Bugsnag.notify("Elastic Tracker Not Initialised", (event) => {
          event.addMetadata("mediamanager", {
            initialised,
            settings: settings?.media_manager,
            mediamanager: typeof mediamanager,
            container: element,
          });
        });
      }
      return 1000;
    }
    return 0;
  }

  let count = 0;
  /**
   * Initialise the Media Manager player.
   * @param {object} settings drupalSettings object.
   */
  async function initialisePlayer(settings) {

    if (typeof mediamanager !== "function" && count > 15) {
      console.error(`Media Manager library not loaded. Attempted ${count} times. Video ID: ${settings.media_manager.video_token}`);
      return;
    }
    if (typeof mediamanager !== "function" && count <= 15) {
      console.warn(`Media Manager library not loaded. Trying again in 1s, attempt no. ${count}.`);
      count += 1;
      setTimeout(() => initialisePlayer(settings), 1000);
      return;
    }

    const embed = mediamanager(settings.media_manager.container_id);

    // Default autoplay value.
    let autoStart = settings.media_manager.autoplay;
    const userIsAnonymous = settings.user.uid === 0;

    // Check if autoplay is in the URL params and override.
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    if (urlParams.has("autoplay")) {
      autoStart = urlParams.get("autoplay") === "true";
    }
    if (userIsAnonymous) {
      autoStart = !await isSuspectedBot();
    }

    let config = {
      token: settings.media_manager.video_token,
      displayTitle: settings.media_manager.show_title,
      displayDescription: settings.media_manager.show_description,
      logo: settings.media_manager.show_logo,
      floating: settings.media_manager.float,
      mute: settings.media_manager.mute,
      sharing: settings.media_manager.sharing,
      playbackRateControls: settings.media_manager.show_playback_rate,
      autoStart: autoStart,
      downloadable: false,
      aspectRatio: "16:9",
      environment: settings.media_manager.environment,
    };

    if (settings.live_event) {
      // Premiere date is number of seconds, we need milliseconds.
      const premiereDate = new Date(settings.live_event.start * 1000);
      const premiereEndDate = new Date(settings.live_event.end * 1000);
      const dateNow = new Date();
      let autoStart = true;
      let skipTo = 0;
      let live = true;

      if (premiereDate > dateNow) {
        // Hasn't started yet. Don't autoplay.
        autoStart = false;
      } else if (premiereEndDate > dateNow) {
        // Skip to the current time in the video.
        skipTo = Math.floor(Date.now() / 1000) - settings.live_event.start;
      } else {
        // No longer a live event.
        live = false;
      }

      if (live) {
        config = {
          ...config,
          ...{ autoStart, skipTo, live },
        };
      }
    }

    if (settings.media_manager.media_manager_api_url) {
      config.host = settings.media_manager.media_manager_api_url;
    }

    if (settings.media_manager.about_url) {
      config.aboutLink = settings.media_manager.about_url;
    }

    if (settings.media_manager.about_text) {
      config.aboutText = settings.media_manager.about_text;
    }

    if (settings.media_manager.color) {
      config.overrideColor = settings.media_manager.color;
    }

    if (settings.media_manager.title) {
      config.overrideTitle = settings.media_manager.title;
    }

    if (settings.media_manager.description) {
      config.overrideDescription = settings.media_manager.description;
    }

    if (settings.media_manager.image) {
      config.overrideImage = settings.media_manager.image;
    }

    if (settings.media_manager.premiere) {
      embed.setEventListener.on("ready", () => {
        const player = jwplayer();
        player.addButton(
          '<svg xmlns="http://www.w3.org/2000/svg" class="jw-svg-icon" viewBox="0 0 320 512"><path d="M267.5 440.6c9.5 7.9 22.8 9.7 34.1 4.4s18.4-16.6 18.4-29V96c0-12.4-7.2-23.7-18.4-29s-24.5-3.6-34.1 4.4l-192 160L64 241V96c0-17.7-14.3-32-32-32S0 78.3 0 96V416c0 17.7 14.3 32 32 32s32-14.3 32-32V271l11.5 9.6 192 160z"/></svg>',
          "Start from beginning",
          () => {
            player.seek(0);
          },
          "startAgain",
        );
      });
    }

    if (settings.media_manager.analytics) {
      settings.media_manager.analytics = replaceAnalyticsTokens(settings.media_manager.analytics);
      config.analytics = settings.media_manager.analytics;
    }

    embed.setEventListener.on("ready", () => {
      const message = {
        event: "ready",
        playerId: settings.media_manager.container_id,
      };
      postMessage(message, "*");
    });

    embed.setEventListener.on("play", () => {
      const message = {
        event: "play",
        playerId: settings.media_manager.container_id,
      };
      postMessage(message, "*");
    });

    embed.setEventListener.on("pause", () => {
      const message = {
        event: "pause",
        playerId: settings.media_manager.container_id,
      };
      postMessage(message, "*");
    });

    embed.setEventListener.on("complete", () => {
      const message = {
        event: "ended",
        playerId: settings.media_manager.container_id,
      };
      postMessage(message, "*");
    });

    embed.setup(config);
    mmInstances[settings.media_manager.container_id] = embed;

    // Trigger user search in case it's needed.
    runUserSearch(settings, embed);
  }

  Drupal.behaviors.mediaManagerVideoPlayer = {
    attach: (context, settings) => {

      // Feature flag is enabled, use the new player.
      if (settings.media_manager?.enablePlayerv3 && settings?.media_manager?.enablePlayerv3 === true) {
        return;
      }

      if (initialised || !settings.media_manager || !settings.media_manager.video_token || !settings.media_manager.container_id) {
        const element = document.querySelector(`#${settings?.media_manager?.container_id}`);
        const accessParamExists = getUrlParam("access");
        const UtParamExists = getUrlParam("UT");

        if ((accessParamExists || UtParamExists) && typeof Bugsnag !== "undefined" && !initialised) {
          console.error("Media Manager video player not initialised.");
          Bugsnag.notify("Media Manager video player not initialised.", (event) => {
            event.addMetadata("mediamanager", {
              initialised,
              settings: settings?.media_manager,
              mediamanager: typeof mediamanager,
              container: element || "Not found",
            });
          });
        }
        return;
      }

      checkPlayerAccessibility(settings);

      if (!once("containerId", `#${settings.media_manager.container_id}`)) {
        return;
      }

      initialised = true;

      const timeoutDelay = checkElasticReady(settings);
      setTimeout(() => initialisePlayer(settings), timeoutDelay);
    },
  };
})(Drupal, once);
